import { GameRouteService } from "../services/GameRouteService";
import { css } from "../helpers/webComponents";
import { PlaneTicketRequest } from "@shared/types";
import { CanvasComponent } from "./CanvasComponent";
import { GameEventService } from "../services/GameEventService";

/** CSS affecting the {@link PcComponent} */
const styles: string = css`
    :host {
        position: fixed; /* Ensure it's not constrained by the document flow */
        top: 57%; /* Adjust as needed */
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 10; 
        pointer-events: auto; /* Ensure it’s interactive */
    }


    .container {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        width: fit-content;
        height: 100%;
        max-width: 60vh;
        min-width: 40vh;

        border-width: 40px;
        border-image: url('/assets/img/ui/pc-border.png') 37 54 45 50 fill;
        border-style: solid;
        z-index: 10;
        filter: drop-shadow(10px 10px 15px rgba(0, 0, 0, 0.8));
    }

    .status {
        position: absolute;
    }

    .message-box {
        position: fixed;
        background-color: #474747;
        padding: 20px;
        border: 2px solid #007bff;
        border-radius: 10px;
        box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.2);
        text-align: center;
        display: none;  
        z-index: 1000;
        cursor: default;
    }

    .message-box button {
        margin-top: 10px;
        padding: 5px 10px;
        border: none;
        background-color: #007bff;
        color: white;
        cursor: pointer;
        border-radius: 5px;
    }

    button {
        padding: 10px 20px;
        font-size: 1rem;
        font-weight: bold;
        border: none;
        border-radius: 5px;
        color: white;
        cursor: pointer;
        color: white;
        cursor: pointer;
       background-color: #0056b3;
    }
    button:hover {
        background-color: #007bff;

    }

    img {
        width: 101%;
        height: auto;
        object-fit: contain;
        cursor: default;
        z-index: -5;
    }
`;

export class PcComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /**
     * Create a new instance of the {@link PcComponent}.
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        this.refreshStatus();
    }

    /**
     * Refresh the status of the component.
     * @private
     */
    private refreshStatus(): void {
        this.render();
    }

    /**
     * Render the component's HTML content.
     * @private
     */
    private render(): void {
        if (!this.shadowRoot) return;

        this.shadowRoot.innerHTML = `
        <style>${styles}</style>
        <div class="container">
            <div class="status">
                <button id="buy-ticket">Buy plane ticket</button>
                <button id="turn-pc-off">Off</button>
            </div>
            <img src="assets/img/components/pc-desktop.png" alt="PC" />
            <div id="message-box" class="message-box">
                <p id="message-text"></p>
                <button id="close-message">OK</button>
            </div>
        </div>
    `;

        this.playSound("pc-startup.mp3");

        this.attachEventListeners();
    }

    /**
    * Attach event listeners to the component's elements.
    */
    private attachEventListeners(): void {
        const shadowRoot: ShadowRoot = this.shadowRoot!;
        shadowRoot.getElementById("buy-ticket")
            ?.addEventListener("click", this.handleBuyTicketClick.bind(this));

        shadowRoot.getElementById("turn-pc-off")
            ?.addEventListener("click", () => this.handleTurnPcOffClick());

        shadowRoot.getElementById("close-message")?.addEventListener("click", () => {
            this.playSound("pc-click.mp3");
            shadowRoot.getElementById("message-box")!.style.display = "none";
        });
    }

    /**
    * Handles the click event for buying a plane ticket.
    * It displays a message box with the result of the ticket purchase.
    */
    private async handleBuyTicketClick(): Promise<void> {
        this.playSound("pc-click.mp3");

        const shadowRoot: ShadowRoot = this.shadowRoot!;
        const messageBox: HTMLElement = shadowRoot.getElementById("message-box")!;
        const messageText: HTMLElement | null = shadowRoot.getElementById("message-text");

        messageBox.style.display = "block";

        const { isTicketBought, isTicketAlreadyBought }: PlaneTicketRequest =
            await this._gameRouteService.buyPlaneTicket();

        this.playSound("pc-message.mp3");
        messageText!.textContent = this.getTicketMessage(isTicketBought, isTicketAlreadyBought);
    }

    /**
     * Handles the click event for turning of the pc.
     */
    private async handleTurnPcOffClick(): Promise<void> {
        this.playSound("pc-click.mp3");
        this.playSound("pc-shutdown.mp3");

        await this._gameRouteService.turnPcOff();
        const canvasComponent: CanvasComponent | null = document.querySelector("game-root");

        if (canvasComponent && canvasComponent.shadowRoot) {
            const gameCanvas: HTMLElement | null = canvasComponent.shadowRoot.querySelector("game-canvas");
            if (gameCanvas) {
                void (gameCanvas as CanvasComponent).refreshGameState();
            }
        }
    }

    /**
     * Get the message to display in the message box based on the ticket purchase result.
     * @param isBought
     * @param alreadyBought
     * @private
     */
    private getTicketMessage(isBought: boolean, alreadyBought: boolean): string {
        return isBought
            ? "You have bought a plane ticket!"
            : alreadyBought
                ? "You already have a plane ticket!"
                : "You need a wallet to buy a plane ticket!";
    }

    private playSound(name: string): void {
        this._gameEventService.playSound(name);
    }
}
